import React from 'react'
import { v4 as uuidv4 } from 'uuid'
import { useForm } from '@mantine/form'
import { useMutation, useQuery } from '@tanstack/react-query'
import { notifications } from '@mantine/notifications'
import { zodResolver } from 'mantine-form-zod-resolver'
import { ActionIcon, Button, Divider, Group, Modal, Paper, Table, Text } from '@mantine/core'

import {
  ClientBaseType,
  UpdateClientType,
  CreateClientSchema,
  CreateClientType,
  CreateContactType,
  ContactBaseType,
  CreateContactSchema,
  StatusClientType,
} from '@customTypes/client'
import { ModalFormType, ModalProps } from '@customTypes/page'

import DataRepo from '@api/datasource/data'
import queryClient from '@api/datasource/query'

import QueryKeys from '@constants/queryKeys'

import { useIsMobile, useTableMinWidth } from '@hooks/mobile'

import { ErrorService } from '@utils/error'
import { invalidateDataQuery, isLoadingMutation } from '@utils/network'
import { buildSelectOptions, filterSelect } from '@utils/form'

import Input from '@components/shared/input'
import { ClientTypeOptions, PriorityClient } from '@constants/client'
import ConfirmationModal from '@components/shared/confirmationModal'
import moment from 'moment'
import { IconCheck, IconCircleFilled, IconEdit, IconTrash } from '@tabler/icons-react'
import { $ } from '@utils/styles'

const INITIAL: CreateClientType = {
  name: '',
  description: '',
  contacts: [],
  status: null,
  priority: 'medium',
  location: '',
  phone: '',
  ruc: '',
  type: 'prospect',
}

const INITIAL_CONTACT: CreateContactType = {
  uid: '',
  name: '',
}

export type ClientFormDataType = {
  client?: ClientBaseType
  initialStatus?: StatusClientType
}

const ClientForm = (props: ModalProps<ClientFormDataType, ClientBaseType>) => {
  const { open, modal, data, size = 'lg', onSubmitted, onClose: outerOnClose } = props

  const isMobile = useIsMobile()

  const minWidth = useTableMinWidth(isMobile)

  const [modalContact, setModalContact] = React.useState<
    ModalFormType<ClientBaseType['contacts'][0]>
  >({
    open: false,
  })

  const [confirmExit, setConfirmExit] = React.useState(false)

  const formClient = useForm<CreateClientType>({
    initialValues: INITIAL,
    validate: zodResolver(CreateClientSchema),
  })

  const formContact = useForm<CreateContactType>({
    initialValues: INITIAL_CONTACT,
    validate: zodResolver(CreateContactSchema),
  })

  const statusQuery = useQuery({
    placeholderData: [],
    queryKey: [QueryKeys.GET_STATUS_KEY],
    queryFn: () => DataRepo.clientEntityService.getStatus(),
  })

  const clientCreateMutation = useMutation<ClientBaseType, ErrorService, CreateClientType>({
    mutationFn: async (data) => {
      const response = await DataRepo.clientEntityService.createClient(data)

      await queryClient.invalidateQueries({
        predicate: (query) => [QueryKeys.GET_CLIENTS_KEY].includes(query.queryKey[0] as string),
        refetchType: 'all',
      })

      onSubmitted?.(response)

      return response
    },
    onSettled: (_, error) => {
      if (error) {
        return notifications.show({
          color: 'red',
          title: 'Error',
          message: error.message ?? 'Error al crear la cliente',
        })
      }

      notifications.show({
        color: 'green',
        title: 'Éxito',
        message: 'Cliente creada correctamente',
      })

      onClose()
    },
  })

  const clientUpdateMutation = useMutation<void, ErrorService, UpdateClientType>({
    mutationFn: async (data) => {
      const response = await DataRepo.clientEntityService.updateClient(data)

      await invalidateDataQuery<ClientBaseType>({
        queryKeys: [QueryKeys.GET_CLIENT_KEY, data.uid],
        data: data as ClientBaseType,
        exact: true,
      })

      await queryClient.invalidateQueries({
        predicate: (query) => [QueryKeys.GET_CLIENTS_KEY].includes(query.queryKey[0] as string),
        refetchType: 'all',
      })

      return response
    },
    onSettled: (_, error) => {
      if (error) {
        return notifications.show({
          color: 'red',
          title: 'Error',
          message: error.message ?? 'Error al actualizar el cliente',
        })
      }

      notifications.show({
        color: 'green',
        title: 'Éxito',
        message: 'Cliente actualizado correctamente',
      })

      onSubmitted?.()

      onClose?.()
    },
  })

  React.useEffect(() => {
    if (open && data) {
      if (data?.client) {
        formClient.setValues(data.client)
      } else if (data.initialStatus) {
        formClient.setFieldValue('status', data.initialStatus, {
          forceUpdate: true,
        })
      }
    } else if (!open) {
      formClient.setValues(INITIAL)
    }

    formContact.setValues(INITIAL_CONTACT)
    formClient.resetDirty()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, data])

  React.useEffect(() => {
    if (modalContact.open && modalContact.data) {
      formContact.setValues(modalContact.data)
    } else if (!modalContact.open) {
      formContact.setValues(INITIAL_CONTACT)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalContact.data])

  const isLoading = isLoadingMutation(clientCreateMutation, clientUpdateMutation)

  const content = (
    <form
      onSubmit={formClient.onSubmit((values) => {
        if (data?.client) {
          clientUpdateMutation.mutate({
            ...values,
            uid: data.client.uid,
          })
        } else {
          clientCreateMutation.mutate(values as CreateClientType)
        }
      })}
    >
      <div className="cd-flex cd-flex-col cd-gap-y-[1rem]">
        <Input
          className="cd-basis-[100%] md:cd-basis-1/2"
          label="Nombre"
          placeholder="Ingrese el nombre"
          typeInput="text"
          {...formClient.getInputProps('name')}
          onBlur={() => formClient.validateField('name')}
        />
        <div className="cd-flex cd-flex-col cd-gap-y-[1rem] md:cd-flex-row md:cd-gap-x-[1rem]">
          <Input
            className="cd-basis-[100%] md:cd-basis-1/2"
            data={buildSelectOptions({
              data: Object.values(ClientTypeOptions),
              value: 'value',
              label: 'label',
            })}
            filter={(input) =>
              filterSelect({
                search: input.search,
                options: input.options,
              })
            }
            label="Tipo de cliente"
            loading={isLoadingMutation(statusQuery)}
            placeholder="Seleccione el tipo de cliente"
            typeInput="select"
            {...formClient.getInputProps('type')}
            value={formClient.values.type}
            onBlur={() => formClient.validateField('type')}
            onChange={(value) => {
              const typeData = ClientTypeOptions[value as keyof typeof ClientTypeOptions]

              if (!typeData) {
                notifications.show({
                  color: 'red',
                  title: 'Error',
                  message: 'Tipo de cliente inválido',
                })
              }

              formClient.setFieldValue('type', typeData.value as CreateClientType['type'], {
                forceUpdate: true,
              })
            }}
          />

          <Input
            className="cd-basis-[100%] md:cd-basis-1/2"
            data={buildSelectOptions({
              data: statusQuery.data || [],
              value: 'uid',
              label: 'name',
            })}
            disabled={Boolean(data?.initialStatus)}
            filter={(input) =>
              filterSelect({
                search: input.search,
                options: input.options,
              })
            }
            label="Estado"
            loading={isLoadingMutation(statusQuery)}
            placeholder="Seleccione el estado"
            readOnly={Boolean(data?.initialStatus)}
            renderOption={(option) => (
              <div className={$('cd-w-full cd-flex cd-items-center cd-gap-x-2 cd-justify-between')}>
                <div className="cd-flex cd-items-center cd-gap-x-2">
                  <IconCircleFilled
                    color={
                      statusQuery.data?.find((s) => s.uid === option.option.value)?.color || '#000'
                    }
                    size={16}
                  />
                  <Text
                    className={$(
                      'cd-text-gray-800 dark:cd-text-gray-200',
                      option.checked && '!cd-font-bold',
                    )}
                  >
                    {option.option.label}
                  </Text>
                </div>
                {option.checked && <IconCheck />}
              </div>
            )}
            typeInput="select"
            {...formClient.getInputProps('status')}
            value={formClient.values.status?.uid || ''}
            onBlur={() => formClient.validateField('status')}
            onChange={(value) => {
              const status = statusQuery.data?.find((s) => s.uid === value)

              formClient.setFieldValue('status', status || null, {
                forceUpdate: true,
              })
            }}
          />
        </div>

        <Input
          className="cd-basis-[100%] md:cd-basis-1/2"
          label="Dirección"
          placeholder="Ingrese la dirección"
          typeInput="text"
          {...formClient.getInputProps('location')}
          onBlur={() => formClient.validateField('location')}
        />

        <Input
          autosize
          label="Descripción"
          maxRows={4}
          minRows={2}
          placeholder="Ingrese la descripción"
          typeInput="textarea"
          {...formClient.getInputProps('description')}
          onBlur={() => formClient.validateField('description')}
        />

        <div className="cd-flex cd-flex-col cd-gap-y-[1rem] md:cd-flex-row md:cd-gap-x-[1rem]">
          <Input
            className="cd-basis-[100%] md:cd-basis-1/2"
            label="Email"
            placeholder="Ingrese el email"
            typeInput="text"
            {...formClient.getInputProps('email')}
            onBlur={() => formClient.validateField('email')}
          />
          <Input
            searchable
            allowDeselect={false}
            className="cd-basis-[100%] md:cd-basis-1/2"
            data={buildSelectOptions({
              data: Object.values(PriorityClient),
              value: 'value',
              label: 'label',
            })}
            filter={(input) =>
              filterSelect({
                search: input.search,
                options: input.options,
              })
            }
            label="Prioridad"
            nothingFoundMessage="No se encontraron resultados"
            placeholder="Ingrese la prioridad"
            typeInput="select"
            {...formClient.getInputProps('priority')}
            onBlur={() => formClient.validateField('priority')}
          />
        </div>

        <div className="cd-flex cd-flex-col cd-gap-y-[1rem] md:cd-flex-row md:cd-gap-x-[1rem]">
          <Input
            className="cd-basis-[100%] md:cd-basis-1/2"
            label="RUC"
            placeholder="Ingrese el RUC"
            typeInput="text"
            {...formClient.getInputProps('ruc')}
            onBlur={() => formClient.validateField('ruc')}
          />

          <Input
            className="cd-basis-[100%] md:cd-basis-1/2"
            label="Teléfono"
            placeholder="Ingrese el teléfono"
            type="tel"
            typeInput="text"
            {...formClient.getInputProps('phone')}
            onBlur={() => formClient.validateField('phone')}
          />
        </div>

        <Divider label="Contactos" />

        <div className="cd-flex cd-flex-row md:cd-justify-end">
          <Button
            fullWidth={isMobile}
            type="button"
            onClick={() => setModalContact({ open: true })}
          >
            Agregar contacto
          </Button>
        </div>

        <Paper withBorder>
          <Table.ScrollContainer minWidth={minWidth + (isMobile ? 450 : 0)} type="native">
            <Table striped withColumnBorders withRowBorders className="cd-w-full">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Nombre</Table.Th>
                  <Table.Th>Correo</Table.Th>
                  <Table.Th>Teléfono</Table.Th>
                  <Table.Th>Acciones</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {Boolean(formClient.values.contacts.length) &&
                  formClient.values.contacts.map((contact, index) => (
                    <Table.Tr key={`contact-${index}`}>
                      <Table.Td className={contact.master ? 'cd-font-bold' : ''}>
                        {contact.name} {contact.master && '(Principal)'}
                      </Table.Td>
                      <Table.Td className={contact.master ? 'cd-font-bold' : ''}>
                        {contact.email}
                      </Table.Td>
                      <Table.Td className={contact.master ? 'cd-font-bold' : ''}>
                        {contact.phone}
                      </Table.Td>
                      <Table.Td className="">
                        <Group justify="center">
                          <ActionIcon
                            variant="transparent"
                            onClick={() => {
                              setModalContact({ open: true, data: contact })
                            }}
                          >
                            <IconEdit size={18} />
                          </ActionIcon>
                          <ActionIcon
                            color="red"
                            variant="transparent"
                            onClick={() => {
                              const newContacts = formClient.values.contacts.filter(
                                (item) => item.uid !== contact.uid,
                              )
                              formClient.setFieldValue('contacts', newContacts)
                            }}
                          >
                            <IconTrash size={18} />
                          </ActionIcon>
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                {!formClient.values.contacts.length && (
                  <Table.Tr>
                    <Table.Td className="cd-text-center" colSpan={4}>
                      No se han agregado contactos
                    </Table.Td>
                  </Table.Tr>
                )}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        </Paper>

        <Modal
          centered
          opened={modalContact.open}
          size="md"
          title="Agregar contacto"
          onClose={() => setModalContact({ open: false })}
        >
          <form>
            <div className="cd-flex cd-flex-col cd-gap-y-[1rem]">
              <Input
                label="Nombre"
                placeholder="Ingrese el nombre"
                typeInput="text"
                {...formContact.getInputProps('name')}
                onBlur={() => formContact.validateField('name')}
              />

              <Input
                label="Es contacto principal"
                labelPosition="left"
                typeInput="switch"
                {...formContact.getInputProps('master', { type: 'checkbox' })}
                checked={Boolean(formContact.values.master)}
                onBlur={() => formContact.validateField('master')}
                onChange={(value) => {
                  const newContacts = formClient.values.contacts.map((contact) => {
                    if (contact.uid === formContact.values.uid) {
                      return {
                        ...contact,
                        master: Boolean(value.target.checked),
                      }
                    }
                    return {
                      ...contact,
                      master: false,
                    }
                  })
                  formContact.setFieldValue('master', Boolean(value.target.checked))
                  formClient.setFieldValue('contacts', newContacts, { forceUpdate: true })
                }}
              />

              <Input
                label="Correo"
                placeholder="Ingrese el correo"
                type="email"
                typeInput="text"
                {...formContact.getInputProps('email')}
                onBlur={() => formContact.validateField('email')}
              />

              <Input
                label="Teléfono"
                placeholder="Ingrese el teléfono"
                type="tel"
                typeInput="text"
                {...formContact.getInputProps('phone')}
                onBlur={() => formContact.validateField('phone')}
              />
            </div>

            <Button
              fullWidth
              className="cd-mt-[1rem]"
              disabled={!formContact.isValid()}
              loaderProps={{ type: 'dots' }}
              onClick={() => {
                formContact.onSubmit((values) => {
                  if (modalContact.data) {
                    const targetId = modalContact.data.uid
                    const newContacts = formClient.values.contacts.map((contact) => {
                      if (contact.uid === targetId) {
                        return {
                          ...contact,
                          ...values,
                        }
                      }
                      return contact
                    })
                    formClient.setFieldValue('contacts', newContacts)
                  } else {
                    const newContact: ContactBaseType = {
                      ...values,
                      uid: uuidv4(),
                      updatedAt: moment().unix(),
                    }

                    formClient.setFieldValue('contacts', [
                      ...formClient.values.contacts,
                      newContact,
                    ])
                  }
                  formContact.reset()
                  setModalContact({ open: false })
                })()
              }}
            >
              {modalContact.data ? 'Guardar contacto' : 'Crear contacto'}
            </Button>
          </form>
        </Modal>

        <Button
          className="cd-mt-[1rem]"
          disabled={!formClient.isValid()}
          loaderProps={{ type: 'dots' }}
          loading={isLoading}
          type="submit"
        >
          {data ? 'Guardar cliente' : 'Crear cliente'}
        </Button>
      </div>
    </form>
  )

  if (modal) {
    return (
      <Modal
        centered
        fullScreen={isMobile}
        opened={Boolean(open)}
        size={size}
        title={data?.client?.uid ? 'Editar cliente' : 'Agregar cliente'}
        onClose={() => {
          if (formClient.isDirty()) {
            setConfirmExit(true)
          } else {
            onClose()
          }
        }}
      >
        {content}
        <ConfirmationModal
          cancelColor="gray"
          cancelText="Cancelar"
          confirmColor="red"
          confirmText="Descartar"
          open={confirmExit}
          title="Descartar cambios"
          onCancel={() => setConfirmExit(false)}
          onConfirm={onClose}
        >
          <Text className="cd-text-base">
            Has realizado cambios en el formulario, ¿Deseas cerrarlo y descartar los cambios?
          </Text>
        </ConfirmationModal>
      </Modal>
    )
  }

  return content

  function onClose() {
    outerOnClose?.()
    setConfirmExit(false)
    formClient.reset()
  }
}

export default ClientForm
